import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

type Item = {
  id: string;
  type: 'video' | 'article' | 'note';
  title: string;
  url: string | null;
  description: string | null;
};

type Provider = 'youtube' | 'vimeo' | null;

// A video only counts once 90% of its runtime has actually been played.
const WATCH_THRESHOLD_RATIO = 0.9;
// Articles/resources need genuine reading time (the backend enforces this too).
const READ_THRESHOLD_SECONDS = 45;
const FLUSH_INTERVAL_MS = 5000;

const getYouTubeId = (url: string): string | null => {
  const m = url.match(/(?:youtu\.be\/|v=|shorts\/|embed\/|\/v\/)([\w-]{11})/);
  return m ? m[1] : null;
};

const getVimeoId = (url: string): string | null => {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
};

export function CoachContentViewerModal({ item, onClose }: { item: Item | null; onClose: () => void }) {
  if (!item) return null;
  const url = item.url || '';

  const { provider, embed } = useMemo(() => {
    if (item.type !== 'video') return { provider: null as Provider, embed: null as string | null };
    const yt = getYouTubeId(url);
    if (yt) {
      return {
        provider: 'youtube' as Provider,
        embed: `https://www.youtube-nocookie.com/embed/${yt}?rel=0&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`,
      };
    }
    const vm = getVimeoId(url);
    if (vm) return { provider: 'vimeo' as Provider, embed: `https://player.vimeo.com/video/${vm}?api=1` };
    return { provider: null as Provider, embed: null as string | null };
  }, [item.type, url]);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const watchedRef = useRef(false);
  const cumulativeSecondsRef = useRef(0); // total across sessions
  const pendingDeltaRef = useRef(0); // unsent local delta
  const durationRef = useRef(0);
  const lastTickRef = useRef<number | null>(null);
  const tickIntervalRef = useRef<number | null>(null);
  const flushIntervalRef = useRef<number | null>(null);
  const positionRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const readSecondsRef = useRef(0);
  const scrolledToEndRef = useRef(false);
  const endedRef = useRef(false);
  const endedSentRef = useRef(false);
  const [marked, setMarked] = useState(false);
  const [resumed, setResumed] = useState(0);
  const [pct, setPct] = useState(0);

  const markWatchedLocal = () => {
    if (watchedRef.current) return;
    watchedRef.current = true;
    setMarked(true);
    // Let coach/player dashboards refresh their completion numbers.
    window.dispatchEvent(new CustomEvent('assignment-completion-changed'));
  };

  // Flush pending watched seconds to the backend; backend sets viewed_at when
  // the threshold is crossed OR the player reached the end of the video.
  const flushProgress = async () => {
    const delta = Math.round(pendingDeltaRef.current);
    const sendEnded = endedRef.current && !endedSentRef.current;
    if (delta <= 0 && !sendEnded) return;
    pendingDeltaRef.current -= delta;
    if (sendEnded) endedSentRef.current = true;
    try {
      const { data } = await supabase.rpc('record_watch_progress' as any, {
        p_content_id: item.id,
        p_seconds_delta: Math.max(delta, 0),
        p_duration: Math.round(durationRef.current) || null,
        p_position: Math.round(positionRef.current) || 0,
        p_progress_pct: scrolledToEndRef.current ? 100 : 0,
        p_ended: endedRef.current,
      });
      const payload = data as { watched_seconds?: number; viewed_at?: string | null; progress_pct?: number } | null;
      if (payload?.viewed_at) markWatchedLocal();
      if (typeof payload?.watched_seconds === 'number') {
        cumulativeSecondsRef.current = payload.watched_seconds;
      }
      if (typeof payload?.progress_pct === 'number') setPct(payload.progress_pct);
    } catch {
      // Re-queue the delta so we try again on the next flush.
      pendingDeltaRef.current += delta;
      if (sendEnded) endedSentRef.current = false;
    }
  };

  // Seed cumulative seconds from saved progress when modal opens.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.rpc('get_watch_progress' as any, { p_content_id: item.id });
        if (cancelled) return;
        const payload = data as { watched_seconds?: number; duration_seconds?: number | null; viewed_at?: string | null; progress_pct?: number } | null;
        if (payload?.watched_seconds) {
          cumulativeSecondsRef.current = payload.watched_seconds;
          setResumed(payload.watched_seconds);
        }
        if (payload?.duration_seconds) durationRef.current = payload.duration_seconds;
        if (payload?.viewed_at) markWatchedLocal();
        if (typeof payload?.progress_pct === 'number') setPct(payload.progress_pct);
      } catch {
        /* noop */
      }
    })();
    return () => { cancelled = true; };
  }, [item.id]);

  // Articles / resources: opening only records a start. Completion requires real
  // reading time (or reaching the end of the text after a minimum dwell).
  useEffect(() => {
    if (item.type === 'video') return;
    (async () => {
      try { await supabase.rpc('mark_content_viewed' as any, { p_content_id: item.id }); } catch { /* noop */ }
    })();

    const tick = window.setInterval(() => {
      if (document.hidden) return;
      readSecondsRef.current += 1;
      pendingDeltaRef.current += 1;
      const el = scrollRef.current;
      if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 24) scrolledToEndRef.current = true;
      const enough =
        readSecondsRef.current >= READ_THRESHOLD_SECONDS ||
        (scrolledToEndRef.current && readSecondsRef.current >= 15);
      if (enough) flushProgress();
    }, 1000) as unknown as number;

    const flush = window.setInterval(flushProgress, FLUSH_INTERVAL_MS) as unknown as number;
    return () => {
      window.clearInterval(tick);
      window.clearInterval(flush);
      flushProgress();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, item.type]);

  // Video playback tracking + periodic flush.
  useEffect(() => {
    if (!provider || !embed) return;

    const post = (msg: any) => {
      iframeRef.current?.contentWindow?.postMessage(typeof msg === 'string' ? msg : JSON.stringify(msg), '*');
    };

    const maybeMark = () => {
      const total = cumulativeSecondsRef.current + pendingDeltaRef.current;
      const dur = durationRef.current;
      if (dur > 0) setPct(Math.min(100, Math.floor((total / dur) * 100)));
      // Reaching the last seconds of the runtime is treated as "ended" even if
      // the player never delivered an explicit ended event.
      if (dur > 0 && positionRef.current >= Math.max(dur - 2, dur * 0.99)) {
        endedRef.current = true;
      }
      if (!watchedRef.current && dur > 0 && (endedRef.current || total / dur >= WATCH_THRESHOLD_RATIO)) {
        flushProgress(); // push immediately so coach sees it
      }
    };

    const startTicking = () => {
      if (tickIntervalRef.current) return;
      lastTickRef.current = Date.now();
      tickIntervalRef.current = window.setInterval(() => {
        const now = Date.now();
        if (lastTickRef.current != null) {
          pendingDeltaRef.current += (now - lastTickRef.current) / 1000;
        }
        lastTickRef.current = now;
        maybeMark();
      }, 500) as unknown as number;
    };

    const stopTicking = () => {
      if (tickIntervalRef.current) {
        window.clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = null;
      }
      lastTickRef.current = null;
    };

    flushIntervalRef.current = window.setInterval(flushProgress, FLUSH_INTERVAL_MS) as unknown as number;

    const onMessage = (event: MessageEvent) => {
      const src = event.source;
      if (src && iframeRef.current && src !== iframeRef.current.contentWindow) return;

      let data: any = event.data;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch { return; }
      }
      if (!data || typeof data !== 'object') return;

      if (provider === 'youtube') {
        if (data.event === 'onReady' || data.event === 'initialDelivery') {
          post({ event: 'listening', id: 'sportara-yt' });
          post({ event: 'command', func: 'getDuration', args: [] });
        }
        if (data.event === 'infoDelivery' && data.info) {
          if (typeof data.info.currentTime === 'number') positionRef.current = data.info.currentTime;
          if (typeof data.info.duration === 'number' && data.info.duration > 0) {
            durationRef.current = data.info.duration;
          }
          if (typeof data.info.playerState === 'number') {
            if (data.info.playerState === 1) startTicking();
            else if (data.info.playerState === 0) { endedRef.current = true; stopTicking(); flushProgress(); }
            else stopTicking();
          }
        }
        if (data.event === 'onStateChange') {
          if (data.info === 1) startTicking();
          else if (data.info === 0) { endedRef.current = true; stopTicking(); flushProgress(); }
          else stopTicking();
        }
      } else if (provider === 'vimeo') {
        if (data.event === 'ready') {
          post({ method: 'addEventListener', value: 'play' });
          post({ method: 'addEventListener', value: 'pause' });
          post({ method: 'addEventListener', value: 'ended' });
          post({ method: 'addEventListener', value: 'timeupdate' });
          post({ method: 'getDuration' });
        }
        if (data.event === 'play') startTicking();
        if (data.event === 'pause') { stopTicking(); flushProgress(); }
        if (data.event === 'ended') { endedRef.current = true; stopTicking(); flushProgress(); }
        if (data.event === 'timeupdate' && data.data) {
          if (typeof data.data.duration === 'number') durationRef.current = data.data.duration;
          if (typeof data.data.seconds === 'number') positionRef.current = data.data.seconds;
        }
        if (data.method === 'getDuration' && typeof data.value === 'number') {
          durationRef.current = data.value;
        }
      }
    };

    const onVisibility = () => { if (document.hidden) { stopTicking(); flushProgress(); } };
    const onBeforeUnload = () => { stopTicking(); flushProgress(); };

    window.addEventListener('message', onMessage);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onBeforeUnload);

    const iframe = iframeRef.current;
    const onLoad = () => {
      if (provider === 'youtube') post({ event: 'listening', id: 'sportara-yt' });
    };
    iframe?.addEventListener('load', onLoad);

    return () => {
      window.removeEventListener('message', onMessage);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onBeforeUnload);
      iframe?.removeEventListener('load', onLoad);
      stopTicking();
      if (flushIntervalRef.current) {
        window.clearInterval(flushIntervalRef.current);
        flushIntervalRef.current = null;
      }
      // Final flush on unmount/close.
      flushProgress();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, embed, item.id]);

  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60);
    return `${m}:${r.toString().padStart(2, '0')}`;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-3xl rounded-2xl overflow-hidden flex flex-col max-h-[calc(100dvh-2rem)]"
          style={{ backgroundColor: 'rgba(15,20,40,0.98)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <div className="flex items-center justify-between p-4 border-b border-white/10 flex-shrink-0 gap-3">
            <h3 className="font-display font-bold text-base text-foreground truncate flex-1">{item.title}</h3>
            {marked && (
              <span className="inline-flex items-center gap-1 text-[11px] font-display font-bold text-green-400 shrink-0">
                <CheckCircle2 className="w-3.5 h-3.5" /> Watched
              </span>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 flex-shrink-0"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto">
            {embed ? (
              <div className="relative w-full" style={{ aspectRatio: '16 / 9' }}>
                <iframe
                  ref={iframeRef}
                  src={embed}
                  title={item.title}
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : item.type === 'note' ? (
              <div className="p-5">
                <p className="text-sm text-foreground/90 font-body whitespace-pre-wrap">
                  {item.description || 'No content.'}
                </p>
              </div>
            ) : (
              <div className="p-5 space-y-3">
                {item.description && (
                  <p className="text-sm text-foreground/80 font-body whitespace-pre-wrap">{item.description}</p>
                )}
                {url && (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-display font-bold text-sm"
                    style={{ backgroundColor: '#3b6ef8' }}
                  >
                    Open Link <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}
          </div>

          {!embed && (
            <div className="p-3 border-t border-white/10 flex-shrink-0">
              <p className="text-[11px] font-body text-foreground/60">
                {marked ? 'Marked complete ✓' : 'Read through to the end to complete this item'}
              </p>
            </div>
          )}

          {embed && url && (
            <div className="p-3 border-t border-white/10 flex-shrink-0 flex items-center justify-between gap-2">
              <p className="text-[11px] font-body text-foreground/60">
                {marked
                  ? 'Counted as watched ✓'
                  : resumed > 0
                    ? `${pct}% watched — finish 90% to complete this item`
                    : 'Watch 90% of the video to complete this item'}
              </p>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-display font-bold text-primary hover:underline shrink-0"
              >
                Open on source <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
